import * as yup from 'yup';
import {
  checkIfHasLastname,
  cpfIsValid,
  hasOnlyLetters,
  validateCellphone,
} from './string';
import {
  checkIfIsLegalAge,
  isAgeIntervalValid,
  isMaxAgeValid,
  validateDate,
} from './date';
import { isAfter, parse } from 'date-fns';

export const isFullName = () => {
  return yup
    .string()
    .required('Insira seu nome completo')
    .test(
      'has_lastname',
      'Você precisa preencher o nome completo',
      (value?: string) => checkIfHasLastname(String(value))
    )
    .test('only_letters', 'Apenas letras são permitidas', (value?: string) => {
      return !/\d/.test(String(value));
    })
    .test(
      'no_special_caracter',
      'Caracteres especiais não são permitidos',
      (value?: string) => hasOnlyLetters(String(value))
    );
};

export const isEmail = () => {
  return yup
    .string()
    .email('Insira um email válido')
    .required('Insira o seu email válido para continuar');
};

export const isCpf = () => {
  return yup
    .string()
    .when('birthday', {
      is: (birthday: string) => {
        return checkIfIsLegalAge(birthday);
      },
      then: yup.string().required('Preecha um CPF válido'),
    })
    .test('is_cpf', 'Preencha um CPF válido', (value: string | undefined) => {
      return value !== '' ? cpfIsValid(value || '') : true;
    })
    .required('O CPF é obrigatório');
};

export const isDateValid = () => {
  return yup
    .string()
    .required('Insira as informações para continuar')
    .test('date_valid', 'A data não é válida', (value?: string) =>
      validateDate(String(value))
    )
    .test(
      'less_than_today',
      'A data deve ser menor que a data atual',
      (value?: string) => {
        const birthdayDate = parse(String(value), 'dd/MM/yyyy', new Date());

        return !isAfter(birthdayDate, new Date());
      }
    );
};

export const isDateIntervalValid = (minAge: number, maxAge: number) => {
  return yup
    .string()
    .required('Preencha a data de nascimento')
    .test(
      'birthday_valid',
      'A data de nascimento não é válida',
      (value?: string) => validateDate(String(value))
    )
    .test(
      'less_than_today',
      'A data deve ser menor que a data atual',
      (value?: string) => {
        const birthdayDate = parse(String(value), 'dd/MM/yyyy', new Date());

        return !isAfter(birthdayDate, new Date());
      }
    )
    .test(
      'interval_age',
      `A idade mínima é de ${minAge} anos e a máxima é de ${maxAge} anos`,
      (value?: string) => {
        if (minAge && maxAge && value) {
          return isAgeIntervalValid(value, minAge, maxAge);
        }

        return true;
      }
    );
};

interface BirthDateParams {
  maxAge?: number;
}

export const isBirthDateValid = ({ maxAge }: BirthDateParams) => {
  return yup
    .string()
    .required('Preencha a data de nascimento')
    .test(
      'birthday_valid',
      'A data de nascimento não é válida',
      (value?: string) => validateDate(String(value))
    )
    .test(
      'less_than_today',
      'A data deve ser menor que a data atual',
      (value?: string) => {
        const birthdayDate = parse(String(value), 'dd/MM/yyyy', new Date());

        return !isAfter(birthdayDate, new Date());
      }
    )
    .test(
      'max_age',
      `A idade máxima permitida é de ${maxAge} anos`,
      (value?: string) => {
        if (maxAge && value) {
          return isMaxAgeValid(value, maxAge);
        }

        return true;
      }
    );
};

export const isCellPhone = () => {
  return yup
    .string()
    .test(
      'is_cellphone_valid',
      'Insira um número de celular válido com DDD',
      (value: string | undefined) => {
        return value !== '' ? validateCellphone(value || '') : true;
      }
    );
};

export const isValidExpiryDate = () => {
  return yup
    .string()
    .required('A data de vencimento é obrigatória')
    .test({
      name: 'validateDate',
      message: 'Data de vencimento inválida',
      test: (value) => {
        if (value) {
          const [month, year] = value.split('/');

          if (!month || !year) {
            return false;
          }

          const currYear = Number(new Date().getFullYear());

          if (Number(month) > 12 || Number(year) < currYear) {
            return false;
          }

          const currMonth = new Date().getMonth() + 1;

          if (Number(year) === currYear && Number(month) < currMonth) {
            return false;
          }

          return true;
        }

        return false;
      },
    });
};
